import HomePageSectionHeader from "components/homePageSectionHeader/homePageSectionHeader";
import ProCard from "components/proCard/proCard";
import classNames from "classnames";

export interface ProCardData {
  headline: string;
  description: string;
  image: {
    url: string;
    alt?: string | null;
    width?: number;
    height?: number;
    blurUpThumb?: string;
  };
  buttonText: string;
  buttonUrl: string;
}

export interface ProSectionData {
  sectionTitle: string;
  sectionButtonText: string;
  sectionButtonUrl: string;
  cards: ProCardData[];
}

export interface ProSectionProps {
  data: ProSectionData;
  onSectionButtonClick?: () => void;
  onCardButtonClick?: (cardIndex: number) => void;
  className?: string;
  "data-testid"?: string;
}

export interface ProSectionContentProps {
  data: ProSectionData;
  onSectionButtonClick?: () => void;
  onCardButtonClick?: (cardIndex: number) => void;
  "data-testid"?: string;
}

const ProSectionContent: React.FC<ProSectionContentProps> = ({
  data,
  onSectionButtonClick,
  onCardButtonClick,
  "data-testid": dataTestId,
}) => {
  return (
    <div
      className="mx-auto w-full max-w-content-mobile md:max-w-content lg:max-w-container mdDesktop:max-w-container"
      data-testid={dataTestId}
    >
      <HomePageSectionHeader
        title={data.sectionTitle}
        buttonText={data.sectionButtonText}
        buttonUrl={data.sectionButtonUrl}
        onButtonClick={onSectionButtonClick}
        theme="pro"
        className="mb-4"
      />

      <div
        className={classNames(
          "-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5",
          "scrollbar-hide",
          "md:mx-0 md:grid md:snap-none md:grid-cols-2 md:overflow-visible md:px-0",
          "mdDesktop:grid-cols-4",
          "lg:grid-cols-4"
        )}
      >
        {data.cards.map((card, index) => (
          <div
            key={`pro-card-${index}`}
            className={classNames(
              "flex-shrink-0 snap-start",
              "w-[calc(100vw-80px)]",
              "md:w-auto md:flex-shrink",
              "h-full"
            )}
          >
            <ProCard
              headline={card.headline}
              description={card.description}
              image={card.image}
              buttonText={card.buttonText}
              buttonUrl={card.buttonUrl}
              onButtonClick={() => onCardButtonClick?.(index)}
              className="h-full"
              data-testid={`pro-card-${index}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const ProSection: React.FC<ProSectionProps> = ({
  data,
  onSectionButtonClick,
  onCardButtonClick,
  className,
  "data-testid": dataTestId,
}) => {
  return (
    <section
      className={classNames(
        "w-full bg-mono-05 px-5 pb-16 pt-0 md:px-5 lg:px-0 mdDesktop:px-5",
        className
      )}
    >
      <ProSectionContent
        data={data}
        onSectionButtonClick={onSectionButtonClick}
        onCardButtonClick={onCardButtonClick}
        data-testid={dataTestId}
      />
    </section>
  );
};

export default ProSection;
